import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import Panel from './Panel.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { getHangarSubmission } from '@/services/deals.service.js';
import { HANGAR_SUBMISSION_UPDATED_EVENT } from './SubmissionPanel.jsx';

// Locations & Coverages panel for hangar/property deals (HANGAR-SUBMISSION-
// FIXES §C1). Property deals show the insured LOCATIONS — never Fleet/Pilots
// (those belong to the account's separate aircraft deals and bled into the
// property view before the line-gated layout). Data comes from the same
// GET hangar-submission read the SubmissionPanel uses; inFlightDedup collapses
// the concurrent mount into one request.

function fmtMoney(v) {
  const n = Number(String(v == null ? '' : v).replace(/[$,\s]/g, ''));
  if (!isFinite(n) || n <= 0) return String(v || '');
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function airportDisplay(loc) {
  if (loc.airport_name && loc.airport) return `${loc.airport_name} (${loc.airport})`;
  return loc.airport_name || loc.airport || '';
}

function addressLine(a) {
  if (!a) return '';
  return [a.street, a.street2, a.city, a.state, a.zip].filter(Boolean).join(', ');
}

function Stat({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-text-dim">{label}</div>
      <div className="text-sm text-text">{String(value)}</div>
    </div>
  );
}

function LocationCard({ loc, idx, count }) {
  const building = loc.building || {};
  const risk = loc.risk || {};
  const covs = Array.isArray(loc.coverages) ? loc.coverages : [];
  const flags = [
    risk.premises_liability === 'Yes' ? 'Premises liability requested' : '',
    risk.fuel_tanks_above_ground === 'Yes' ? 'Above-ground fuel tanks' : '',
    risk.painting_in_hangar === 'Yes' ? 'Painting in hangar' : '',
  ].filter(Boolean);

  return (
    <div className="rounded-card border border-border px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <MapPin className="h-3.5 w-3.5 shrink-0 text-text-muted" />
        <span className="text-sm font-medium text-text">
          {count > 1 ? `Location ${idx + 1} — ` : ''}{airportDisplay(loc) || 'Location'}
        </span>
        {flags.map((f) => (
          <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>
        ))}
      </div>
      {addressLine(loc.address) ? (
        <div className="mt-0.5 text-xs text-text-muted">{addressLine(loc.address)}</div>
      ) : null}

      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-5">
        <Stat label="Year built" value={building.year_built} />
        <Stat label="Construction" value={building.construction} />
        <Stat label="Area (sq ft)" value={building.area_sqft ? Number(String(building.area_sqft).replace(/,/g, '')).toLocaleString('en-US') : ''} />
        <Stat label="Sprinklered" value={building.sprinklered} />
        <Stat label="Alarm" value={building.alarm_system} />
      </div>

      {covs.length > 0 || loc.misc_coverage ? (
        <div className="mt-2 border-t border-border pt-2">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-text-dim">Coverages requested</div>
          <div className="space-y-0.5">
            {covs.map((c) => (
              <div key={c.key} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-text-muted">{c.label}</span>
                <span className="font-medium text-text">{fmtMoney(c.limit)}</span>
              </div>
            ))}
            {loc.misc_coverage ? (
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-text-muted">{loc.misc_coverage.description || 'Miscellaneous coverage'}</span>
                <span className="font-medium text-text">{fmtMoney(loc.misc_coverage.limit)}</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {loc.lienholder ? (
        <div className="mt-2 border-t border-border pt-2 text-xs text-text-muted">
          <span className="font-medium text-text">{loc.lienholder.type || 'Lienholder'}:</span>{' '}
          {loc.lienholder.name || ''}{addressLine(loc.lienholder.address) ? ` — ${addressLine(loc.lienholder.address)}` : ''}
        </div>
      ) : null}
    </div>
  );
}

export default function HangarLocationsPanel({ dealId }) {
  const [state, setState] = useState({ loading: !!dealId, locations: [] });
  // Bumped when SubmissionPanel saves an Edit-submission overlay so this
  // panel re-pulls the merged locations.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const bump = () => setRefreshTick((t) => t + 1);
    window.addEventListener(HANGAR_SUBMISSION_UPDATED_EVENT, bump);
    return () => window.removeEventListener(HANGAR_SUBMISSION_UPDATED_EVENT, bump);
  }, []);

  useEffect(() => {
    let aborted = false;
    if (!dealId) {
      setState({ loading: false, locations: [] });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true }));
    getHangarSubmission(dealId)
      .then((d) => {
        if (!aborted) setState({ loading: false, locations: Array.isArray(d?.locations) ? d.locations : [] });
      })
      .catch(() => {
        if (!aborted) setState({ loading: false, locations: [] });
      });
    return () => {
      aborted = true;
    };
  }, [dealId, refreshTick]);

  const { loading, locations } = state;
  return (
    <Panel title="Locations & Coverages" count={locations.length || undefined}>
      {loading ? (
        <p className="text-xs italic text-text-dim">Loading locations…</p>
      ) : locations.length === 0 ? (
        <p className="text-xs italic text-text-dim">No locations on file for this submission.</p>
      ) : (
        <div className="space-y-2">
          {locations.map((loc, i) => (
            <LocationCard key={i} loc={loc} idx={i} count={locations.length} />
          ))}
        </div>
      )}
    </Panel>
  );
}
